import { Client } from "../interfaces/client";
import { CriminalDetail } from "../interfaces/criminalDetail";
import { CriminalOrder } from "../interfaces/criminalOrder";
import { Order } from "../interfaces/order";
import { fetchWithAuth } from "./fetchWithAuth";
import { convertBlobToBase64 } from "./helpers";

const APP_URL = process.env.REACT_APP_APP_URL || "";
const DEV_APP_URL = process.env.REACT_APP_DEV_APP_URL || "";
const PROD_APP_URL = process.env.REACT_APP_PROD_APP_URL || "";

const siteBaseUrl =
  window.location.hostname === "localhost"
    ? APP_URL
    : window.location.hostname === new URL(PROD_APP_URL).hostname
      ? PROD_APP_URL
      : DEV_APP_URL;

const API_URL = process.env.REACT_APP_API_URL || "";
const DEV_API_URL = process.env.REACT_APP_DEV_API_URL || "";
const PROD_API_URL = process.env.REACT_APP_PROD_API_URL || "";

const apiBaseUrl =
  window.location.hostname === "localhost"
    ? API_URL
    : window.location.hostname === new URL(PROD_APP_URL).hostname
      ? PROD_API_URL
      : DEV_API_URL;

export const getCriminalOrderByOrderID = async (
  orderID: number,
): Promise<CriminalOrder> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/CriminalOrder/GetCriminalOrderByOrderID/${orderID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  // If the backend returns 404 Not Found, send back a blank default object
  if (response.status === 404) {
    return {
      criminalOrderID: 0,
      orderID,
      years: 0,
      sentToInvoicing: false,
      invoiceDate: new Date(),
      finalReportDate: new Date(),
      finalReportUrl: "",
      billedDate: undefined,
      adminReviewed: false,
      inAdminReview: false,
    };
  }

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Fetch Criminal Order by OrderID Error:", errorText);
    throw new Error("Failed to fetch criminal order.");
  }

  const criminalOrder: CriminalOrder = await response.json();
  return criminalOrder;
};

// Not currently used
export const getCriminalOrderIDByOrderID = async (
  orderID: number,
): Promise<number> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const response = await fetch(
    `${apiBaseUrl}/api/CriminalOrder/GetCriminalOrderIDByOrderID/${orderID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (response.status === 404) {
    return 0;
  }

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Fetch Criminal Order ID Error:", errorText);
    throw new Error("Failed to fetch criminal order ID.");
  }

  const criminalOrderID: number = await response.json();
  return criminalOrderID;
};

export const getCriminalOrderByID = async (
  criminalOrderID: number,
): Promise<CriminalOrder> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/CriminalOrder/GetCriminalOrderByID/${criminalOrderID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Criminal Order ID ${criminalOrderID} was not found.`);
    }

    const errorText = await response.text();
    console.error("Fetch Criminal Order by ID Error:", errorText);
    throw new Error("Failed to fetch criminal order.");
  }

  const order: CriminalOrder = await response.json();
  return order;
};

export const updateCriminalOrder = async (
  criminalOrder: CriminalOrder,
): Promise<CriminalOrder> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined.");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/CriminalOrder/UpdateCriminalOrder`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(criminalOrder),
    },
  );

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Criminal Order was not found to update.`);
    }

    const errorText = await response.text();
    console.error("Update Criminal Order Error:", errorText);
    throw new Error("Failed to update the criminal order.");
  }

  const updatedOrder: CriminalOrder = await response.json();
  return updatedOrder;
};

export const generateCriminalFinalReport = async (
  client: Client,
  order: Order,
  criminalOrder: CriminalOrder,
  criminalDetails: CriminalDetail[],
): Promise<Blob | null> => {
  try {
    const formData = {
      client,
      order,
      criminalOrder,
      criminalDetails,
    };

    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("User is not authenticated");
    }

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/CriminalOrder/GenerateCriminalFinalReport`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      console.error("Failed to generate the criminal final report.", response);
      return null;
    }

    const result = await response.json();

    if (result.success && result.pdfBase64) {
      // Convert Base64 to Blob URL
      const byteCharacters = atob(result.pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      return blob;
    } else {
      console.error("Error from server:", result.message);
      return null;
    }
  } catch (error) {
    console.error("Error generating the criminal final report:", error);
    return null;
  }
};

export const sendCriminalFinalReport = async (
  client: Client,
  criminalOrder: CriminalOrder,
  pdfBlob: Blob,
  byMail: boolean,
): Promise<boolean> => {
  try {
    const finalReportBase64 = await convertBlobToBase64(pdfBlob);

    const formData = {
      client,
      criminalOrder,
      finalReportBase64,
      byMail,
      baseUrl: `${siteBaseUrl}?portal=client`,
    };

    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("User is not authenticated");
    }

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/CriminalOrder/SendCriminalFinalReport`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      console.error("Failed to send the criminal final report.");
      return false;
    }

    if (byMail) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "CriminalFinalReport.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return true;
    } else {
      const result = await response.json();

      if (result.success) {
        console.log("Final report sent successfully.");
        return true;
      } else {
        console.error("Error from server:", result.message);
        return false;
      }
    }
  } catch (error) {
    console.error("Error sending the criminal final report:", error);
    return false;
  }
};

export const sendCriminalFinalReportSuran = async (
  client: Client,
  criminalOrder: CriminalOrder,
  pdfBlob: Blob,
  byMail: boolean,
): Promise<boolean> => {
  try {
    const finalReportBase64 = await convertBlobToBase64(pdfBlob);

    const formData = {
      client,
      criminalOrder,
      finalReportBase64,
      byMail,
      baseUrl: `${siteBaseUrl}?portal=client`,
    };

    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("User is not authenticated");
    }

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/CriminalOrder/SendCriminalFinalReportSuran`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      console.error("Failed to send the criminal final report.");
      return false;
    }

    if (byMail) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "CriminalFinalReport.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return true;
    } else {
      const result = await response.json();

      if (result.success) {
        console.log("Final report sent successfully.");
        return true;
      } else {
        console.error("Error from server:", result.message);
        return false;
      }
    }
  } catch (error) {
    console.error("Error sending the criminal final report:", error);
    return false;
  }
};

export const deleteCriminalOrder = async (
  criminalOrderID: number,
): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/CriminalOrder/DeleteCriminalOrder/${criminalOrderID}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Criminal Order ID ${criminalOrderID} was not found.`);
    }

    const errorText = await response.text();
    console.error("Delete Criminal Detail Error:", errorText);
    throw new Error(
      `Failed to delete criminal detail with ID ${criminalOrderID}.`,
    );
  }
};

export const createCriminalOrder = async (
  orderID: number,
): Promise<CriminalOrder> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined.");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/CriminalOrder/CreateCriminalOrder/${orderID}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Create Criminal Order Error:", errorText);
    throw new Error("Failed to create the criminal order.");
  }

  const createdOrder: CriminalOrder = await response.json();
  return createdOrder;
};
